import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { TASK2_ACTIVITIES } from '../data/sprintActivities';

function wordCount(str) {
  if (!str) return 0;
  return str.trim().split(/\s+/).filter(Boolean).length;
}

export default function SprintTask2() {
  const navigate = useNavigate();
  const [sprint, setSprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [responses, setResponses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/sprint/status');
        if (!data.sprint) { navigate('/sprint'); return; }
        setSprint(data.sprint);
        if (data.sprint.task2?.selectedActivities?.length) {
          setSelected(data.sprint.task2.selectedActivities);
        }
        if (data.sprint.task2?.responses?.length) {
          setResponses(data.sprint.task2.responses);
        }
      } catch { navigate('/sprint'); }
      setLoading(false);
    })();
  }, [navigate]);

  const toggleActivity = (idx) => {
    setSelected(prev => {
      if (prev.includes(idx)) return prev.filter(i => i !== idx);
      if (prev.length >= 4) return prev;
      return [...prev, idx];
    });
  };

  const handleConfirmActivities = async () => {
    if (selected.length !== 4) {
      setError('Please select exactly 4 activities.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/sprint/task2/select-activities', { activities: selected });
      setSprint(data.sprint);
      setResponses(data.sprint.task2.responses);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to confirm activities.');
    }
    setSaving(false);
  };

  const updateResponse = (activityIndex, field, value) => {
    setResponses(prev => prev.map(r =>
      r.activityIndex === activityIndex ? { ...r, [field]: value } : r
    ));
  };

  const handleSaveResponses = useCallback(async () => {
    if (!responses.length) return;
    try {
      await api.put('/sprint/task2/save-responses', { responses });
    } catch { /* silent save */ }
  }, [responses]);

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // Validate all fields
    for (const resp of responses) {
      const title = TASK2_ACTIVITIES[resp.activityIndex]?.title || `Activity ${resp.activityIndex + 1}`;
      if (!resp.whatDidYouDo || !resp.whatDidYouLearn || !resp.whatWillYouDoNext) {
        setError(`Please fill all three fields for "${title}".`);
        return;
      }
      if (wordCount(resp.whatDidYouDo) > 150) {
        setError(`"What did you do?" for "${title}" exceeds 150 words.`);
        return;
      }
      if (wordCount(resp.whatDidYouLearn) > 150) {
        setError(`"What did you learn?" for "${title}" exceeds 150 words.`);
        return;
      }
      if (wordCount(resp.whatWillYouDoNext) > 100) {
        setError(`"What will you do next?" for "${title}" exceeds 100 words.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      // Save first, then submit
      await api.put('/sprint/task2/save-responses', { responses });
      const { data } = await api.post('/sprint/task2/submit');
      setSprint(data.sprint);
      setSuccess('Task 2 submitted successfully! Nitin will review your responses before the Zoom session.');
    } catch (e) {
      setError(e.response?.data?.error || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.loadingWrap}><div style={s.spinner} /></div>
      </div>
    );
  }

  const status = sprint?.task2?.status || 'not_started';
  const isLocked = sprint?.task2?.activitiesLocked;
  const isApproved = status === 'approved';
  const isSubmitted = status === 'submitted' || status === 'under_review';
  const needsRevision = status === 'needs_revision';
  const canEdit = !isApproved && !isSubmitted;

  return (
    <div style={s.page}>
      <button onClick={() => navigate('/sprint')} style={s.backBtn}>&larr; Back to Sprint</button>

      <div style={s.hero}>
        <p style={s.tagline}>SPRINT — TASK 2</p>
        <h1 style={s.title}>Building Your Professional Foundation</h1>
        <p style={s.subtitle}>Complete within 3 months of starting Sprint</p>
        <p style={s.deadlineText}>
          Deadline: {new Date(sprint.task2Deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Status banners */}
      {isApproved && (
        <div style={s.approvedBanner}>Task 2 has been approved by Nitin.</div>
      )}
      {isSubmitted && (
        <div style={s.submittedBanner}>Your responses are with Nitin. He will review them before your Zoom session.</div>
      )}
      {needsRevision && (
        <div style={s.revisionBanner}>
          <p style={{ margin: 0, fontWeight: '600' }}>Revision requested</p>
          {sprint.task2.reviewNote && <p style={{ margin: '6px 0 0', fontSize: '13px' }}>{sprint.task2.reviewNote}</p>}
        </div>
      )}

      {/* Step 1: Activity Selection (if not locked) */}
      {!isLocked && (
        <div style={s.sectionCard}>
          <p style={{ ...s.bodyText, fontWeight: '700', color: 'var(--navy)', marginBottom: '4px' }}>Choose Your 4 Activities</p>
          <p style={{ ...s.bodyText, color: 'var(--text-2)', fontSize: '13px', marginBottom: '16px' }}>
            Select exactly 4. This cannot be changed after confirmation. ({selected.length}/4 selected)
          </p>

          <div style={s.activityGrid}>
            {TASK2_ACTIVITIES.map((act, i) => {
              const isSelected = selected.includes(i);
              return (
                <div
                  key={i}
                  onClick={() => toggleActivity(i)}
                  style={{
                    ...s.activityCard,
                    borderColor: isSelected ? 'var(--navy)' : 'var(--border)',
                    background: isSelected ? 'var(--gold-light)' : '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <div style={s.activityTop}>
                    <span style={{ ...s.activityNum, background: isSelected ? 'var(--navy)' : 'var(--bg)', color: isSelected ? '#fff' : 'var(--text-3)' }}>{i + 1}</span>
                    <div style={{ ...s.checkbox, borderColor: isSelected ? 'var(--navy)' : 'var(--border)', background: isSelected ? 'var(--navy)' : '#fff' }}>
                      {isSelected && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </div>
                  </div>
                  <p style={{ ...s.bodyText, fontWeight: '600', color: 'var(--navy)', fontSize: '13px', marginBottom: '4px' }}>{act.title}</p>
                  <p style={{ ...s.bodyText, color: 'var(--text-2)', fontSize: '12px' }}>{act.shortDesc}</p>
                </div>
              );
            })}
          </div>

          {error && <p style={s.error}>{error}</p>}

          <button
            onClick={handleConfirmActivities}
            disabled={selected.length !== 4 || saving}
            className="primary-btn"
            style={{ ...s.submitBtn, opacity: selected.length !== 4 ? 0.5 : 1 }}
          >
            {saving ? 'Confirming...' : 'Confirm Your 4 Activities'}
          </button>
        </div>
      )}

      {/* Step 2: Responses (if locked) */}
      {isLocked && (
        <>
          <p className="section-cap" style={{ marginBottom: '14px' }}>Your 4 Activities — Submit 3 Answers Each</p>

          {responses.map((resp) => {
            const act = TASK2_ACTIVITIES[resp.activityIndex];
            if (!act) return null;
            return (
              <div key={resp.activityIndex} style={s.responseCard}>
                <div style={s.responseHeader}>
                  <span style={s.responseNum}>{resp.activityIndex + 1}</span>
                  <p style={{ ...s.bodyText, fontWeight: '700', color: 'var(--navy)' }}>{act.title}</p>
                </div>

                {/* Field 1 */}
                <div style={s.fieldWrap}>
                  <label style={s.fieldLabel}>What did you do?</label>
                  {canEdit ? (
                    <textarea
                      value={resp.whatDidYouDo || ''}
                      onChange={(e) => updateResponse(resp.activityIndex, 'whatDidYouDo', e.target.value)}
                      onBlur={handleSaveResponses}
                      placeholder="Specific. Named. Dated."
                      rows={3}
                      style={s.textarea}
                    />
                  ) : (
                    <p style={s.readonlyText}>{resp.whatDidYouDo}</p>
                  )}
                  <p style={{ ...s.wordCount, color: wordCount(resp.whatDidYouDo) > 150 ? 'var(--danger)' : 'var(--text-3)' }}>
                    {wordCount(resp.whatDidYouDo)}/150 words
                  </p>
                </div>

                {/* Field 2 */}
                <div style={s.fieldWrap}>
                  <label style={s.fieldLabel}>What specifically did you find or learn?</label>
                  {canEdit ? (
                    <textarea
                      value={resp.whatDidYouLearn || ''}
                      onChange={(e) => updateResponse(resp.activityIndex, 'whatDidYouLearn', e.target.value)}
                      onBlur={handleSaveResponses}
                      placeholder="One concrete finding."
                      rows={3}
                      style={s.textarea}
                    />
                  ) : (
                    <p style={s.readonlyText}>{resp.whatDidYouLearn}</p>
                  )}
                  <p style={{ ...s.wordCount, color: wordCount(resp.whatDidYouLearn) > 150 ? 'var(--danger)' : 'var(--text-3)' }}>
                    {wordCount(resp.whatDidYouLearn)}/150 words
                  </p>
                </div>

                {/* Field 3 */}
                <div style={s.fieldWrap}>
                  <label style={s.fieldLabel}>What will you do next because of this?</label>
                  {canEdit ? (
                    <textarea
                      value={resp.whatWillYouDoNext || ''}
                      onChange={(e) => updateResponse(resp.activityIndex, 'whatWillYouDoNext', e.target.value)}
                      onBlur={handleSaveResponses}
                      placeholder="One specific action."
                      rows={2}
                      style={s.textarea}
                    />
                  ) : (
                    <p style={s.readonlyText}>{resp.whatWillYouDoNext}</p>
                  )}
                  <p style={{ ...s.wordCount, color: wordCount(resp.whatWillYouDoNext) > 100 ? 'var(--danger)' : 'var(--text-3)' }}>
                    {wordCount(resp.whatWillYouDoNext)}/100 words
                  </p>
                </div>
              </div>
            );
          })}

          {error && <p style={s.error}>{error}</p>}
          {success && <p style={s.success}>{success}</p>}

          {canEdit && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="primary-btn"
              style={s.submitBtn}
            >
              {submitting ? 'Submitting...' : (needsRevision ? 'Re-submit Task 2' : 'Submit Task 2')}
            </button>
          )}
        </>
      )}
    </div>
  );
}

const s = {
  page: { maxWidth: '720px', margin: '0 auto', padding: '32px 20px 40px' },
  loadingWrap: { display: 'flex', justifyContent: 'center', padding: '80px 0' },
  spinner: { width: '28px', height: '28px', border: '3px solid var(--border)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  backBtn: { background: 'none', border: 'none', color: 'var(--text-2)', fontSize: '13px', cursor: 'pointer', padding: '0', marginBottom: '16px', fontWeight: '500' },
  hero: { marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid var(--border-2)' },
  tagline: { fontSize: '11px', fontWeight: '700', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '8px' },
  title: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '28px', fontWeight: '700', color: 'var(--navy)', marginBottom: '6px', lineHeight: '1.2' },
  subtitle: { fontSize: '14px', color: 'var(--text-2)', marginBottom: '4px' },
  deadlineText: { fontSize: '13px', color: 'var(--gold)', fontWeight: '600' },
  approvedBanner: { background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px', fontWeight: '600' },
  submittedBanner: { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px' },
  revisionBanner: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px' },
  sectionCard: { background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 24px', marginBottom: '20px' },
  bodyText: { fontSize: '14px', color: 'var(--text)', lineHeight: '1.7', margin: 0 },
  activityGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginBottom: '16px' },
  activityCard: { border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', transition: 'all 0.15s' },
  activityTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  activityNum: { width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' },
  checkbox: { width: '20px', height: '20px', borderRadius: '4px', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  responseCard: { background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 24px', marginBottom: '16px' },
  responseHeader: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px' },
  responseNum: { width: '28px', height: '28px', borderRadius: '50%', background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  fieldWrap: { marginBottom: '14px' },
  fieldLabel: { display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-2)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  readonlyText: { fontSize: '13px', color: 'var(--text)', lineHeight: '1.6', padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px', margin: 0 },
  wordCount: { fontSize: '11px', textAlign: 'right', margin: '4px 0 0' },
  error: { color: 'var(--danger)', fontSize: '13px', marginTop: '10px' },
  success: { color: 'var(--success)', fontSize: '13px', marginTop: '10px' },
  submitBtn: { width: '100%', padding: '13px', fontSize: '14px', fontWeight: '700', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', marginTop: '16px' },
};
